import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { ChatMessage } from '../models/chatbot.model';
import { MarkdownToPdfService } from './markdown-to-pdf.service';

@Injectable({
  providedIn: 'root',
})
export class PdfExportService {
  constructor(private markdownToPdfService: MarkdownToPdfService) {}

  /**
   * Export a single chat message to PDF with proper markdown formatting
   */
  async exportMessageToPdf(message: ChatMessage): Promise<void> {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;

    // Add title
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('AEMS Assistant - Chat Response', margin, 30);

    // Add timestamp
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 40);
    pdf.text(`Response Time: ${message.timestamp.toLocaleString()}`, margin, 48);

    let yPosition = 60;

    // Add message type indicator
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    const messageType = message.isUser ? 'User Question:' : 'Assistant Response:';
    pdf.text(messageType, margin, yPosition);
    yPosition += 10;

    // Check if we need a new page before adding content
    if (yPosition > pageHeight - margin - 50) {
      pdf.addPage();
      yPosition = margin;
    }

    // Add formatted message content using markdown service
    try {
      yPosition = this.markdownToPdfService.addMarkdownToPdf(pdf, message.text, yPosition, {
        pageWidth,
        pageHeight,
        margin,
        lineHeight: 6,
      });
    } catch (error) {
      // Fallback to plain text if markdown parsing fails
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      const splitContent = pdf.splitTextToSize(message.text, pageWidth - margin * 2);
      pdf.text(splitContent, margin, yPosition);
      yPosition += splitContent.length * 6 + 10;
    }

    // Add metadata if available
    if (!message.isUser && message.responseTimeMs) {
      yPosition += 10;

      // Check if we need a new page
      if (yPosition > pageHeight - margin - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Response Metadata:', margin, yPosition);
      yPosition += 8;

      pdf.setFont('helvetica', 'normal');
      pdf.text(`Response Time: ${message.responseTimeMs}ms`, margin + 10, yPosition);
      yPosition += 6;

      if (message.tokensUsed) {
        pdf.text(`Tokens Used: ${message.tokensUsed}`, margin + 10, yPosition);
        yPosition += 6;
      }

      if (message.contextUsed) {
        pdf.text(`Query Time: ${message.contextUsed.query_time_ms}ms`, margin + 10, yPosition);
        yPosition += 6;
        pdf.text(`Results: ${message.contextUsed.total_results}`, margin + 10, yPosition);
        yPosition += 6;

        if (message.contextUsed.sql_query) {
          yPosition += 5;

          // Check if we need a new page for SQL
          if (yPosition > pageHeight - margin - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          pdf.setFont('helvetica', 'bold');
          pdf.text('SQL Query:', margin + 10, yPosition);
          yPosition += 8;

          // Use markdown service to format SQL as code block
          const sqlMarkdown = '```sql\n' + message.contextUsed.sql_query + '\n```';
          try {
            this.markdownToPdfService.addMarkdownToPdf(pdf, sqlMarkdown, yPosition, {
              pageWidth,
              pageHeight,
              margin: margin + 10,
              lineHeight: 5,
            });
          } catch (error) {
            // Fallback for SQL formatting
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            const sqlLines = pdf.splitTextToSize(message.contextUsed.sql_query, pageWidth - margin * 3);
            pdf.text(sqlLines, margin + 10, yPosition);
          }
        }
      }
    }

    // Save the PDF
    const fileName = `aems-assistant-response-${new Date().getTime()}.pdf`;
    pdf.save(fileName);
  }

  /**
   * Export entire chat conversation to PDF with proper markdown formatting
   */
  async exportChatToPdf(messages: ChatMessage[]): Promise<void> {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;

    // Add title
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('AEMS Assistant - Chat Conversation', margin, 30);

    // Add metadata
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 40);
    pdf.text(`Total Messages: ${messages.length}`, margin, 48);

    let yPosition = 60;

    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];

      // Check if we need a new page
      if (yPosition > pageHeight - 120) {
        pdf.addPage();
        yPosition = margin;
      }

      // Add message header
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      const messageType = message.isUser ? 'User' : 'Assistant';
      pdf.text(`${messageType} (${message.timestamp.toLocaleString()}):`, margin, yPosition);
      yPosition += 12;

      // Add formatted message content using markdown service
      try {
        yPosition = this.markdownToPdfService.addMarkdownToPdf(pdf, message.text, yPosition, {
          pageWidth,
          pageHeight,
          margin,
          lineHeight: 5,
        });
      } catch (error) {
        // Fallback to plain text if markdown parsing fails
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const splitContent = pdf.splitTextToSize(message.text, pageWidth - margin * 2);

        if (yPosition + splitContent.length * 5 > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.text(splitContent, margin, yPosition);
        yPosition += splitContent.length * 5;
      }

      // Add response metadata for bot messages
      if (!message.isUser && message.responseTimeMs) {
        yPosition += 5;
        pdf.setFontSize(8);
        pdf.setTextColor(100);

        let metadataText = `Response time: ${message.responseTimeMs}ms`;
        if (message.tokensUsed) {
          metadataText += ` | Tokens: ${message.tokensUsed}`;
        }
        if (message.contextUsed?.total_results !== undefined) {
          metadataText += ` | Results: ${message.contextUsed.total_results}`;
        }

        pdf.text(metadataText, margin, yPosition);
        pdf.setTextColor(0);
        yPosition += 8;
      }

      yPosition += 15; // Space between messages
    }

    // Save the PDF
    const fileName = `aems-assistant-conversation-${new Date().getTime()}.pdf`;
    pdf.save(fileName);
  }

  /**
   * Export message with visual formatting (using html2canvas with markdown fallback)
   */
  async exportMessageWithFormattingToPdf(messageElement: HTMLElement, message: ChatMessage): Promise<void> {
    try {
      // Create a canvas from the HTML element
      const canvas = await html2canvas(messageElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });

      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;

      // Add title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AEMS Assistant - Formatted Response', margin, 30);

      // Add timestamp
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 40);

      // Calculate image dimensions to fit on page
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let yPosition = 50;

      // Check if image fits on current page
      if (yPosition + imgHeight > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }

      // Add the image
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', margin, yPosition, imgWidth, imgHeight);

      // Save the PDF
      const fileName = `aems-assistant-formatted-response-${new Date().getTime()}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      // Enhanced fallback to markdown-formatted export instead of plain text
      await this.exportMessageToPdf(message);
    }
  }
}
